<script>
import messages from '@/mixins/messages'
import { timeDateFormattedPretty } from '@/util'
import ReservationCheckoutResource from '@/resources/reservationCheckout'
import PaymentMethod from './PaymentMethod.vue'
import TermsOfServiceLink from './TermsOfServiceLink.vue'
import ApexClubPitch from './ApexClubPitch.vue'
import ApexClubDetail from './ApexClubDetail.vue'
import RelatedImage from './RelatedImage.vue'
import VoucherResource from '@/resources/voucher'

export default {
    name: 'ReservationBookMulti',
    components: {
        PaymentMethod,
        TermsOfServiceLink,
        RelatedImage,
        ApexClubPitch,
        ApexClubDetail,
    },
    computed: {
        hasPaymentMethod() {
            return Object.keys(this.userCardDetails).length > 0
        },
        isApexClubMember() {
            return this.userResource.attributes.credits_subscription_in_good_standing
        },
        bookItButtonDisabled() {
            return this.creatingReservation
        },
        shouldShowReservationBooking() {
            return this.hasPaymentMethod || this.skipPayment
        },
        suggestedSize() {
            return 'md'
        },
        locationCurrencySymbol() {
            return this.checkoutData.currency_symbol || '$'
        },
        quoteReservations() {
            return [...this.reservationResources, ...this.addedReservations]
        },
        upsellRule() {
            return this.checkoutData.pricing_rule_upsell ? this.checkoutData.pricing_rule_upsell.rules[0] : null
        },
        amountUserWillSave() {
            if (this.checkoutData.credits_program_saves) {
                return this.locationCurrencySymbol + this.moneyFormat(this.checkoutData.credits_program_saves)
            }

            return '20%'
        },
        paymentLinkEnabled() {
            if (this.usePaymentLink) {
                return this.checkoutData.total_cents > 0
            }

            return false
        }
    },
    mixins: [messages],
    props: {
        reservationResources: {
            type: Array,
            required: true
        },
        usePaymentLink: Boolean,
        packageDeal: {
            type: Object,
            default: null
        }
    },
    watch: {
        shouldShowReservationBooking() {
            this.$emit('size', this.suggestedSize)
        }
    },
    data() {
        return {
            loading: true,
            selectedCalendarBecomesUnavailable: false,
            creatingReservation: false,
            userResource: null,
            calendarData: {},
            checkoutData: {},
            userCardDetails: {},
            skipPayment: false,
            voucherId: '',
            voucherResource: null,
            voucherEntryVisible: false,
            addedReservations: [],
            addingUpsell: false,
        }
    },
    methods: {
        timeDateFormattedPretty,
        async load() {
            this.userResource = await this.$store.dispatch('getUserResource')
            this.userCardDetails = await this.userResource.getCard()
            return await Promise.all(this.reservationResources.map((reserv) => reserv.resolveRelated()))
        },
        onCardAdded(result) {
            this.userCardDetails = result.card
        },
        async onApexClubRegister(credits) {
            this.$refs.apexClubModal.hide()
            this.userResource.attributes.credits_balance = credits
            this.userResource.attributes.credits_subscription_in_good_standing = true
            await this.updateTotalCost()
        },
        async updateTotalCost() {
            const voucherId = this.voucherResource ? this.voucherResource.id : null
            const packageDealId = this.packageDeal ? this.packageDeal.id : null
            try {
                this.checkoutData = await ReservationCheckoutResource.quote(this.quoteReservations, voucherId, packageDealId)
            } catch (e) {
                const status = e.response?.status
                if (status > 399 && status < 500) {
                    this.$emit('error', this.getResponseMessageFromAxiosException(e))
                } else {
                    throw e
                }
            }
        },
        pricingRuleAmount(rule) {
            return rule.adjustment_type === 'PERCENTAGE' ? `${rule.value}%` : `${this.locationCurrencySymbol}${this.moneyFormat(rule.value)}`
        },
        async addUpsellSessions() {
            this.addingUpsell = true
            try {
                const ReservationResource = require('@/resources/reservation').default
                const additions = this.checkoutData.pricing_rule_upsell.reservations.map((reserv) => new ReservationResource({ calendar: reserv.calendar, slot: reserv.slot }))
                this.addedReservations = [...this.addedReservations, ...additions]
                await this.updateTotalCost()
            } finally {
                this.addingUpsell = false
            }
        },
        async addVoucher() {
            try {
                this.voucherResource = await VoucherResource.detail(this.voucherId.toUpperCase())
                await this.updateTotalCost()
            } catch(e) {
                if(e.response?.status > 399 && e.response?.status < 500) {
                    this.addMessage('warning', 'The discount code you entered was not found or has already been used')
                    await this.removeVoucher()
                } else {
                    throw e
                }
            }
        },
        async removeVoucher() {
            this.voucherId = ''
            this.voucherResource = null
            await this.updateTotalCost()
        },
        async paymentLinkRedirect() {
            const { checkout_id } = this.checkoutData
            const checkout = await ReservationCheckoutResource.detail(checkout_id)
            const url = await checkout.createPaymentLink()
            window.location.href = url
        },
        async createReservation() {
            try {
                this.creatingReservation = true
                const value = this.checkoutData.total_cents / 100
                const commitData = await this.checkoutData.commit()
                await this.$store.dispatch('getUserResource')
                this.$analytics.track('checkout_success', { value, method: 'checkout', currency: 'USD' })
                this.$emit('success', commitData)
            } catch(e) {
                const failMessage = this.getResponseMessageFromAxiosException(e)
                this.$analytics.track('exception_during_booking', { message: e.message, detail: failMessage })
                this.$emit('error', failMessage)
            } finally {
                this.creatingReservation = false
            }
        }
    },
    async mounted() {
        this.loading = true
        await this.load()

        if (this.$store.getters.locationSelected?.attributes?.checkout_method !== 'CAPTURE') {
            this.skipPayment = true
        }
        
        await this.updateTotalCost()
        this.$emit('size', this.suggestedSize)
        this.loading = false
    },
}
</script>

<template>
    <div class="reservation-book-body">
        <b-row v-if="!loading">
            <b-col>
                <b-row>
                    <b-col>
                        <b-alert variant="primary" show v-if="upsellRule">
                            <b-icon icon="tag-fill" /> Ajoutez 1 séance de plus<span v-if="checkoutData.pricing_rule_upsell.reservations.length > 1"> sur {{ checkoutData.pricing_rule_upsell.reservations.length }} simulateurs</span> pour {{ locationCurrencySymbol }}{{ moneyFormat(checkoutData.pricing_rule_upsell.additional_cents) }} de plus et profitez de <strong>{{ upsellRule.name }}</strong> : {{ pricingRuleAmount(upsellRule) }} de réduction par séance (économisez {{ locationCurrencySymbol }}{{ moneyFormat(checkoutData.pricing_rule_upsell.savings_cents) }}).
                            <b-button variant="primary" size="sm" class="d-block mt-2" :disabled="addingUpsell" @click="addUpsellSessions">Ajouter la séance</b-button>
                        </b-alert>
                        <b-table-simple>
                            <b-thead>
                                <b-tr>
                                    <b-th>Simulateur</b-th>
                                    <b-th class="text-right">Prix unitaire</b-th>
                                    <b-th>Qté</b-th>
                                    <b-th class="text-right">Coût</b-th>
                                </b-tr>
                            </b-thead>

                            <b-tbody v-for="(calendarItem, i) in checkoutData.line_items" :key="i">
                                <b-tr>
                                    <b-td class="pb-0"><strong v-b-tooltip.hover :title="calendarItem.calendar_type_name">{{ String(calendarItem.calendar_name) }}</strong></b-td>
                                    <b-td class="pb-0 text-right">{{ locationCurrencySymbol }}{{ moneyFormat(calendarItem.calendar_cost_cents) }}</b-td>
                                    <b-td class="pb-0">{{ calendarItem.calendar_qty }}</b-td>
                                    <b-td class="pb-0 text-right">{{ locationCurrencySymbol }}{{ moneyFormat(calendarItem.calendar_total_cents) }}</b-td>
                                </b-tr>
                                <b-tr>
                                    <b-td colspan="4" class="pt-0 border-0 text-muted">
                                        <small>{{ timeDateFormattedPretty(calendarItem.time, { short: true }) }} <span v-if="true">({{ calendarItem.calendar_location }})</span></small>
                                    </b-td>
                                </b-tr>

                                <b-tr v-if="calendarItem.calendar_booking_fee_cents > 0">
                                    <b-td class="pt-0 border-0 text-muted">
                                        <small>Frais de réservation <span class="ml-1"><a href="javascript:{}" @click="$refs.bookingFeeModal.show()"><b-icon icon="question-circle" /></a></span></small>
                                    </b-td>
                                    <b-td class="pt-0 border-0 text-muted text-right">
                                        <small>{{ locationCurrencySymbol }}{{ moneyFormat(calendarItem.calendar_booking_fee_cents) }}</small>
                                    </b-td>
                                    <b-td class="pt-0 border-0 text-muted">
                                        <small>1</small>
                                    </b-td>
                                    <b-td class="pt-0 border-0 text-muted text-right">
                                        <small>{{ locationCurrencySymbol }}{{ moneyFormat(calendarItem.calendar_booking_fee_cents) }}</small>
                                    </b-td>
                                </b-tr>
                            </b-tbody>


                            <b-tbody>
                                <b-tr>
                                    <b-td colspan="3"><strong>Sous-total</strong></b-td>
                                    <b-td class="text-right">{{ locationCurrencySymbol }}{{ moneyFormat(checkoutData.subtotal_cents) }}</b-td>
                                </b-tr>
                                <b-tr v-if="checkoutData.pricing_rules_applied && checkoutData.pricing_rules_applied.length > 0">
                                    <b-td colspan="4" class="text-right">
                                        <small v-for="(rule, i) in checkoutData.pricing_rules_applied" :key="i" class="text-success d-block">
                                            <b-icon icon="coin" /> <strong>{{ rule.name }}</strong> appliquée (économie de {{ locationCurrencySymbol }}{{ moneyFormat(rule.savings_cents) }})
                                        </small>
                                    </b-td>
                                </b-tr>
                                <b-tr v-if="checkoutData.package_deal_applied && checkoutData.package_deal_applied.length > 0">
                                    <b-td colspan="4" class="text-right">
                                        <small v-for="(deal, i) in checkoutData.package_deal_applied" :key="i" class="text-success d-block">
                                            <b-icon icon="tag-fill" /> <strong>{{ deal.name }}</strong> offre groupée<span v-if="deal.savings_cents"> (économie de {{ locationCurrencySymbol }}{{ moneyFormat(deal.savings_cents) }})</span>
                                        </small>
                                    </b-td>
                                </b-tr>
                                <b-tr v-if="voucherEntryVisible">
                                    <b-td colspan="4" class="text-right">
                                        <small v-if="this.voucherResource" class="text-success"><b-icon icon="coin" /> <strong>{{ this.voucherResource }}</strong> appliqué (économie de {{ locationCurrencySymbol }}{{ moneyFormat(checkoutData.discount_cents) }}) — <a href="javascript:{}" @click="removeVoucher">supprimer</a></small>
                                        <b-input-group v-else>
                                            <b-form-input v-model="voucherId" size="sm" class="col-md-8 offset-md-3 text-uppercase"></b-form-input>
                                            <b-input-group-append>
                                                <b-button variant="outline-primary" size="sm" @click="addVoucher" :disabled="!voucherId">Appliquer la réduction</b-button>
                                            </b-input-group-append>
                                        </b-input-group>
                                    </b-td>
                                </b-tr>
                                <b-tr v-if="this.voucherResource">
                                    <b-td colspan="3"><strong>Sous-total après réductions</strong></b-td>
                                    <b-td class="text-right">{{ locationCurrencySymbol }}{{ moneyFormat(checkoutData.pre_tax_cents) }}</b-td>
                                </b-tr>
                                <b-tr v-if="checkoutData.sales_tax_cents > 0">
                                    <b-td colspan="3"><strong>Taxe de vente</strong></b-td>
                                    <b-td class="text-right">{{ locationCurrencySymbol }}{{ moneyFormat(checkoutData.sales_tax_cents) }}</b-td>
                                </b-tr>
                            </b-tbody>

                            <b-tbody v-if="$appConfigBool('APEX_CLUB_ENABLED', true)">
                                <b-tr>
                                    <b-td class="text-nowrap"><strong>{{ $appConfig('APEX_CLUB_NAME', 'Apex Club') }}</strong> <a href="javascript:{}" v-if="!isApexClubMember" @click="$refs.apexClubModal.show()"><b-icon icon="question-circle" /></a></b-td>
                                    <b-td colspan="3" class="text-right text-nowrap">
                                        <small v-if="isApexClubMember">
                                            <strong><b-avatar icon="currency-exchange" size="1em"></b-avatar> {{ userResource.attributes.credits_balance }}</strong> crédits {{ $appConfig('APEX_CLUB_NAME', 'Apex Club') }} restants
                                        </small>
                                        <small v-else>
                                            <span class="text-muted mr-3"><a href="javascript:{}" @click="$refs.apexClubModal.show()">Économisez {{ amountUserWillSave }} maintenant</a> <b-icon icon="arrow-right" animation="cylon" /></span>
                                            <a href="javascript:{}" @click="$refs.apexClubModal.show()">S'inscrire</a>
                                        </small>
                                    </b-td>
                                </b-tr>
                                <b-tr v-if="isApexClubMember && checkoutData.credits_using > 0">
                                    <b-td><strong>Crédits utilisés</strong></b-td>
                                    <b-td colspan="3" class="text-right text-success"><strong>{{ checkoutData.credits_using }} (d'une valeur de {{ locationCurrencySymbol }}{{ moneyFormat(checkoutData.credits_value_cents) }})</strong></b-td>
                                </b-tr>
                            </b-tbody>
                            
                            <b-tbody>
                                <b-tr>
                                    <b-td colspan="2"><small v-if="!voucherEntryVisible"><a href="javascript:{}" @click="voucherEntryVisible = !voucherEntryVisible">Vous avez un code de réduction ?</a></small></b-td>
                                    <b-td><strong>Total</strong></b-td>
                                    <b-td class="text-right"><strong>{{ locationCurrencySymbol }}{{ moneyFormat(checkoutData.total_cents) }}</strong></b-td>
                                </b-tr>
                            </b-tbody>
                        </b-table-simple>

                    </b-col>
                </b-row>

                <b-row v-if="!paymentLinkEnabled">
                    <b-col>
                        <messages />
                        <b-alert variant="info" :show="hasPaymentMethod && checkoutData.total_cents > 0 && checkoutData.method == 'CAPTURE'">Votre carte <strong>{{ userCardDetails.brand }} se terminant par {{ userCardDetails.last4 }}</strong> sera débitée de <strong>{{ locationCurrencySymbol }}{{ moneyFormat(checkoutData.total_cents) }}</strong></b-alert>
                        <payment-method v-if="!skipPayment && !hasPaymentMethod" cols-lg="12" :user-resource="userResource" @success="onCardAdded">
                            <b-alert variant="success" show><b-icon icon="lock-fill" /> Votre carte ne sera pas encore débitée</b-alert>
                        </payment-method>
                        <b-alert variant="danger" :show="selectedCalendarBecomesUnavailable"><b-icon icon="exclamation-triangle" /> Désolé, l'un des simulateurs que vous avez sélectionnés n'est plus disponible</b-alert>
                    </b-col>
                </b-row>

                <b-row>
                    <b-col>
                        <b-button v-if="paymentLinkEnabled" variant="primary" class="mt-3" @click="paymentLinkRedirect()">Poursuivre le paiement</b-button>
                        <div v-else>
                            <small v-if="skipPayment || hasPaymentMethod" class="text-muted"><terms-of-service-link /></small>
                            <b-button v-if="shouldShowReservationBooking" variant="primary" class="mt-3" @click="createReservation" :disabled="bookItButtonDisabled">{{ Object.keys(checkoutData.line_items || {}).length > 1 ? `Réserver ${Object.keys(checkoutData.line_items || {}).length} simulateurs !` : 'Réserver les simulateurs' }}</b-button>
                        </div>
                    </b-col>
                </b-row>

            </b-col>
        </b-row>

        <b-row v-else>
            <b-col>
                <b-skeleton :width="Math.floor(Math.random() * (100 - 60 + 1) + 60) + '%'" v-for="i in 10" :key="i" />
            </b-col>
        </b-row>

        <b-modal ref="apexClubModal" :title="$appConfig('APEX_CLUB_NAME', 'Apex Club') + ' — Inscription'" size="md" hide-footer no-fade centered>
            <apex-club-pitch @success="onApexClubRegister" >
                <p class="text-muted"><small>(Oui, vous pouvez utiliser vos sessions pour vos amis et votre famille !) — <a href="javascript:{}" v-b-toggle.collapse-faq>Plus d'informations et FAQ</a></small></p>
                <b-collapse id="collapse-faq" class="mb-3">
                    <b-card>
                        <apex-club-detail />
                    </b-card>
                </b-collapse>
            </apex-club-pitch>
        </b-modal>

        <b-modal ref="bookingFeeModal" title="Frais de réservation" size="md" ok-only no-fade centered>
            <p class="lead font-weight-bold">Pourquoi facturons-nous des frais de réservation ?</p>
            <p>Notre objectif est d'offrir la meilleure expérience possible à tous nos pilotes. Les <strong>frais de réservation par simulateur</strong> nous aident à y parvenir de plusieurs manières :</p>
            <p class="lead font-weight-bold">Encourager les sessions plus longues</p>
            <p>L'installation d'un pilote dans un simulateur prend du temps. En favorisant les réservations plus longues, nous réduisons les temps d'arrêt et vous permettons de passer plus de temps à piloter et moins de temps à vous installer.</p>
            <p class="lead font-weight-bold">Optimiser la disponibilité</p>
            <p>Comme nous autorisons le partage, vous pouvez réserver moins de simulateurs pendant plus longtemps afin d'éviter ces frais. Cela permet aux groupes de profiter plus facilement de leurs sessions sans coûts inutiles.</p>
            <p class="lead font-weight-bold">Garantir une expérience de grande qualité</p>
            <p>Ces frais nous aident à entretenir et à améliorer notre équipement afin que chaque session respecte les normes les plus élevées.</p>
            <p class="lead font-weight-bold">Vous souhaitez éviter les frais ?</p>
            <p>
                <strong>Les frais de réservation sont supprimés pour toute réservation de {{ checkoutData.booking_fees_threshold * 30 }} minutes ou plus !</strong>
                <span v-if="!loading && $appConfigBool('APEX_CLUB_ENABLED', true) && !isApexClubMember">
                    Vous pouvez également <a href="javascript:{}" @click="$refs.apexClubModal.show()"><strong>rejoindre notre Apex Club !</strong></a> Les membres bénéficient de la <strong>suppression totale de ces frais</strong>, ainsi que de <strong>plus de 20 % d'économies</strong> sur les réservations et d'autres avantages exclusifs.
                </span>
            </p>
            <p>Nous vous remercions de votre soutien et avons hâte de vous retrouver sur la piste !</p>
        </b-modal>
    </div>
</template>

<style lang="scss">
.reservation-book-body {
    img {
        max-width: 100%;
    }
    
    td.more-info {
        font-size: 0.875em;
        border: none;
    }
}
</style>
